import {
  deriveSessionStatus,
  trainingFailure,
} from '../domain/training.ts';

import type {
  TrainingResult,
} from '../domain/training.ts';

import type {
  TrainingRepository,
} from '../ports/training-repository.ts';

import type {
  TrainingNutritionFact,
  TrainingNutritionSource,
} from '../ports/nutrition-sources.ts';

export type {
  TrainingNutritionFact,
  TrainingNutritionSource,
} from '../ports/nutrition-sources.ts';

function sameContext(
  a: {
    datasetId: string;
    controlEpoch: number;
  },
  b: {
    datasetId: string;
    controlEpoch: number;
  },
) {
  return (
    a.datasetId === b.datasetId &&
    a.controlEpoch === b.controlEpoch
  );
}

export class PublicTrainingNutrition
implements TrainingNutritionSource {
  constructor(
    private readonly planning: Pick<
      TrainingRepository,
      'readPlannedSession'
    >,

    private readonly catalog: Pick<
      TrainingRepository,
      'readContext'
    >,

    private readonly sessions: Pick<
      TrainingRepository,
      'readExecutionForPlannedSession'
    >,
  ) {}

  async readTrainingReference(
    sessionId: string,
  ): Promise<
    TrainingResult<TrainingNutritionFact>
  > {
    const before =
      await this.catalog.readContext();

    if (!before.ok) {
      return before;
    }

    const planned =
      await this.planning
        .readPlannedSession(
          sessionId,
        );

    if (!planned.ok) {
      return planned;
    }

    if (
      !sameContext(
        planned.value.context,
        before.value,
      )
    ) {
      return trainingFailure(
        'DATASET_STALE',
      );
    }

    const execution =
      await this.sessions
        .readExecutionForPlannedSession(
          sessionId,
        );

    if (!execution.ok) {
      return execution;
    }

    if (
      execution.value &&
      !sameContext(
        execution.value.context,
        before.value,
      )
    ) {
      return trainingFailure(
        'DATASET_STALE',
      );
    }

    const status =
      deriveSessionStatus(
        planned.value.session,
        execution.value?.execution,
      );

    if (!status.ok) {
      return status;
    }

    const after =
      await this.catalog.readContext();

    if (
      !after.ok ||
      !sameContext(
        before.value,
        after.value,
      )
    ) {
      return after.ok
        ? trainingFailure(
            'DATASET_STALE',
          )
        : after;
    }

    return {
      ok: true,
      value: {
        sessionId:
          planned.value.session
            .sessionId,

        plannedLocalDate:
          planned.value.session
            .plannedLocalDate,

        status:
          status.value,

        ...(execution.value
          ? {
              startedInstant:
                execution.value.execution
                  .startedInstant,

              ...(execution.value.execution
                .endedInstant
                ? {
                    endedInstant:
                      execution.value
                        .execution
                        .endedInstant,
                  }
                : {}),
            }
          : {}),
      },
    };
  }
}